import React from 'react';
import './ResolveModal.css';

/**
 * Modal offering the two ways to resolve an orphaned account:
 * 1. Relink to a contact that already exists locally (uses the row's CRM id).
 * 2. Re-sync a contact from Zoho by CRM id, then relink to it.
 *
 * After a relink lands, the response reports the downstream Auth0/login-email
 * state. When that outcome needs the admin's attention it is rendered here in
 * place of the two options. The resolved row has already left the orphan
 * report, so this is the one reliable moment to act on it.
 * @returns {React.JSX.Element}
 */
export const ResolveModal = ({
  row,
  busy,
  survivorCrmId,
  onSurvivorCrmIdChange,
  reasonLabel,
  recommendResync,
  recommendRelink,
  outcome,
  onFixAuth0,
  onRelinkExisting,
  onResyncAndRelink,
  onClose,
}) => {
  const hasExistingCrmId = !!row.candidate_user_id && !!row.crm_id;
  const survivorTrimmed = survivorCrmId.trim();
  const canRelinkExisting = !busy && hasExistingCrmId;
  const canResyncAndRelink = !busy && !!row.candidate_user_id && survivorTrimmed.length > 0;

  return (
    <div className="orphan-modal-overlay" onClick={onClose}>
      <div className="orphan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="orphan-modal-header">
          <h3>Resolve orphaned account</h3>
          <button className="orphan-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <dl className="orphan-modal-meta">
          <dt>User</dt>
          <dd>{row.user_email || row.candidate_user_id}</dd>
          <dt>Contact</dt>
          <dd>{row.contact_email || '-'}</dd>
          <dt>Reason</dt>
          <dd>{reasonLabel}</dd>
        </dl>

        {outcome ? (
          <>
            {/* The relink itself succeeded in every branch below, what differs
                is the downstream Auth0/login-email state it uncovered. */}
            {outcome.auth0?.status === 'already_linked' && (
              <p className="orphan-outcome-ok">
                Relinked, and Auth0 login verified for <code>{row.user_email}</code>.
              </p>
            )}

            {outcome.auth0?.status === 'needs_manual' && (
              <div className="orphan-outcome orphan-outcome-danger">
                <strong>Relinked, but this user cannot log in.</strong>
                <p>
                  No Auth0 identity exists for <code>{row.user_email}</code>. Login and
                  password reset are both dead until the identity is found or created.
                </p>
                <button className="cta-button" onClick={onFixAuth0}>Fix Auth0</button>
              </div>
            )}

            {(outcome.auth0?.status === 'skipped' || outcome.auth0?.status === 'error') && (
              <div className="orphan-outcome orphan-outcome-warn">
                <strong>Relinked, but the Auth0 check didn't complete.</strong>
                {outcome.auth0.detail && <p>{outcome.auth0.detail}</p>}
                {outcome.auth0.status === 'error' && (
                  <button className="cta-button" onClick={onFixAuth0}>
                    Retry via Fix Auth0
                  </button>
                )}
              </div>
            )}

            {outcome.email_mismatch && (
              <div className="orphan-outcome orphan-outcome-warn">
                <strong>Login email no longer matches the CRM.</strong>
                <p>
                  The user still signs in with{' '}
                  <code>{outcome.email_mismatch.login_email}</code>; the CRM now has{' '}
                  <code>{outcome.email_mismatch.contact_email}</code>. Logins keep working
                  in this state.
                </p>
                <p>
                  Changing a login email requires backend action. There is deliberately
                  no button for it here. If the user should switch to the new address,
                  file a ticket with both addresses above (this warning is not saved).
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className={`orphan-option ${recommendRelink ? 'orphan-option-recommended' : ''}`}>
              <h4>
                Relink to existing contact
                {recommendRelink && <span className="orphan-badge">Recommended</span>}
              </h4>
              <p>
                The contact already exists locally (CRM id <code>{row.crm_id || '-'}</code>). Link
                the user to it directly.
              </p>
              <button
                className={`cta-button ${canRelinkExisting ? '' : 'disabled-btn'}`}
                disabled={!canRelinkExisting}
                onClick={onRelinkExisting}
              >
                Relink
              </button>
            </div>

            <div className={`orphan-option ${recommendResync ? 'orphan-option-recommended' : ''}`}>
              <h4>
                Re-sync a contact, then relink
                {recommendResync && <span className="orphan-badge">Recommended</span>}
              </h4>
              <p>
                The user's CRM id is unresolved (likely a dead id from a merge). Enter the
                surviving contact's CRM id. It will be pulled in from Zoho and the user relinked
                to it.
              </p>
              <label className="orphan-field">
                <span>Survivor CRM id</span>
                <input
                  type="text"
                  value={survivorCrmId}
                  onChange={(e) => onSurvivorCrmIdChange(e.target.value)}
                  disabled={busy}
                  placeholder="e.g. 123456789012345678"
                />
              </label>
              <button
                className={`cta-button ${canResyncAndRelink ? '' : 'disabled-btn'}`}
                disabled={!canResyncAndRelink}
                onClick={onResyncAndRelink}
              >
                Re-sync &amp; relink
              </button>
            </div>
          </>
        )}

        <div className="orphan-modal-footer">
          <button className="cta-button" onClick={onClose} disabled={busy}>
            {outcome ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResolveModal;
